/**
 * usePageTracking — Anonymous pageview tracking hook.
 *
 * Fires a pageview POST to /v1/analytics/pageview on every route change,
 * UNLESS the client is excluded. Exclusion rules (any single match short-circuits):
 *
 *   1. Hostname is localhost / staging / *.amplifyapp.com
 *   2. User-agent matches a known bot pattern
 *   3. User's email is in EXCLUDED_EMAILS (packages/shared)
 *   4. User's Cognito custom:excludeFromAnalytics attribute is 'true'
 *   5. sessionStorage is unavailable (e.g., private mode restrictions)
 *
 * Session identity is client-side only — a random UUID stored in sessionStorage
 * that exists only for the duration of the browser tab. No cookie is set. No
 * IP, no user-agent, and no user identifier are sent to the backend with
 * pageview events.
 *
 * Failures are silent — analytics must never impact user experience.
 *
 * Must be used inside a Router context (uses useLocation).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  isExcludedHostname,
  isBotUserAgent,
  isExcludedEmail,
} from '@tabletryb/shared';
import { useAuth } from '../context/AuthContext';

const SESSION_STORAGE_KEY = 'tt_analytics_session';
const API_URL = process.env.REACT_APP_API_URL || '';

type UserTier = 'anonymous' | 'trial' | 'paid';

/**
 * Returns the ephemeral session ID, creating one if needed.
 * Returns null if sessionStorage is unavailable (e.g., locked-down privacy mode).
 */
function getOrCreateSessionId(): string | null {
  try {
    let sessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      // crypto.randomUUID is available in all modern browsers (Chrome 92+, Safari 15.4+, Firefox 95+)
      sessionId = crypto.randomUUID();
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return null;
  }
}

/**
 * Extracts the external referrer's hostname from document.referrer.
 * Returns undefined if referrer is empty, same-origin, or malformed.
 */
function getExternalReferrerDomain(): string | undefined {
  if (typeof document === 'undefined' || !document.referrer) return undefined;
  try {
    const url = new URL(document.referrer);
    if (url.hostname === window.location.hostname) return undefined;
    return url.hostname;
  } catch {
    return undefined;
  }
}

export function usePageTracking(): void {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    // --- Exclusion checks ---
    if (isExcludedHostname(window.location.hostname)) return;
    if (typeof navigator !== 'undefined' && isBotUserAgent(navigator.userAgent)) return;
    if (user?.email && isExcludedEmail(user.email)) return;
    if (user?.excludeFromAnalytics) return;

    // --- Session ID (client-side only) ---
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return;

    // --- Determine userTier ---
    // NOTE (v1): Paid vs trial distinction is not yet available in AuthContext.
    // All authenticated users report as 'trial' until a subscription context is wired in.
    const userTier: UserTier = isAuthenticated ? 'trial' : 'anonymous';

    // --- Build payload ---
    const path = location.pathname;
    const isFirstPageview = previousPathRef.current === null;

    const payload: {
      path: string;
      sessionId: string;
      userTier: UserTier;
      referrerPath?: string;
      externalReferrerDomain?: string;
    } = {
      path,
      sessionId,
      userTier,
    };

    if (!isFirstPageview && previousPathRef.current) {
      payload.referrerPath = previousPathRef.current;
    }

    if (isFirstPageview) {
      const externalDomain = getExternalReferrerDomain();
      if (externalDomain) {
        payload.externalReferrerDomain = externalDomain;
      }
    }

    // --- Fire and forget ---
    // keepalive: true ensures the request completes even if the user navigates
    // away before the response arrives. Failures are silent.
    try {
      fetch(`${API_URL}/v1/analytics/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        // Silently ignore — analytics failures must not affect the user
      });
    } catch {
      // Silently ignore synchronous fetch errors (e.g., malformed URL)
    }

    // --- Send page_view to Google Analytics 4 (if loaded) ---
    // Same exclusion rules apply — bots and excluded users are short-circuited
    // above. No-op if gtag.js failed to load or REACT_APP_GA_MEASUREMENT_ID
    // is not set.
    try {
      const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
      if (typeof gtag === 'function') {
        gtag('event', 'page_view', {
          page_path: path,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    } catch {
      // Silently ignore — analytics failures must not affect the user
    }

    previousPathRef.current = path;

    // Deliberately omit `user` and `isAuthenticated` from deps. Route changes
    // fire this effect, and auth state changes almost always coincide with a
    // route change (/login → /app, /app → /login). Including them here would
    // re-fire pageviews on unrelated user-object reference changes.
    // NOTE: react-hooks/exhaustive-deps is not installed — do not use a
    // plugin-specific disable comment (causes build failure).
    // eslint-disable-next-line
  }, [location.pathname]);
}
